import { z } from 'zod'

import { IdSchema, LocalizedSchema } from './common'

// Online store (package flag `store`): prices, colour × size variants with stock, size guides and the cart quote.
// Phase 1 of the e-commerce module. Orders, customers, payments and promo codes come later and build on these shapes:
// a variant's SKU is what an order line points at, and `stock` lives on its own row (product_variants) so an order
// can take it atomically.

/** Every amount is whole rupiah (IDR has no minor unit in practice). */
export const SHOP_CURRENCY = 'IDR' as const

export const SHOP_LIMITS = {
  /** Distinct lines in one cart. */
  cartLines: 30,
  /** Pieces of one variant in one cart. */
  qtyPerLine: 10,
  /** The public catalogue and the quote never show more stock than this ("10+" is enough to decide). */
  stockShown: 10,
  /** "Only N left" from this many down: the default low-stock mark of a variant (the CMS can set its own). */
  lowStock: 3,
  /** Highest low-stock mark a variant can have. */
  lowStockMax: 1_000,
  colors: 12,
  sizes: 16,
  imagesPerColor: 12,
  variants: 300,
} as const

/** Highest price accepted: Rp 1 billion. */
export const MAX_PRICE = 1_000_000_000

export const PriceSchema = z.number().int().min(0).max(MAX_PRICE)

export const SHOP_TAGS = ['new', 'sale', 'bestseller'] as const
export const ShopTagSchema = z.enum(SHOP_TAGS)
export type ShopTag = z.infer<typeof ShopTagSchema>

export const ColorKeySchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,31}$/, 'Lowercase letters, digits and dashes')
export const SizeLabelSchema = z.string().trim().min(1).max(16)
export const SkuSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,47}$/, 'Letters, digits, dots, dashes')
export const HexSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A colour like #1f2a44')

/** One colour of a product: its swatch and its own photos (the gallery switches with the colour). */
export const ShopColorSchema = z.object({
  key: ColorKeySchema,
  name: LocalizedSchema,
  hex: HexSchema,
  images: z.array(IdSchema).max(SHOP_LIMITS.imagesPerColor).default([]),
})
export type ShopColor = z.infer<typeof ShopColorSchema>

/** What a product stores for the store (products.shop). Price and stock have their own columns / rows. */
export const ShopProductSchema = z.object({
  tags: z.array(ShopTagSchema).max(SHOP_TAGS.length).default([]),
  colors: z.array(ShopColorSchema).max(SHOP_LIMITS.colors).default([]),
  /** Sizes in display order (XS…XL, 110…150, or one size). */
  sizes: z.array(SizeLabelSchema).max(SHOP_LIMITS.sizes).default([]),
})
export type ShopProduct = z.infer<typeof ShopProductSchema>
export const emptyShopProduct = (): ShopProduct => ({ tags: [], colors: [], sizes: [] })

/** One sellable colour × size, as the CMS edits it. */
export const VariantInputSchema = z.object({
  color: ColorKeySchema,
  size: SizeLabelSchema,
  sku: SkuSchema,
  stock: z.number().int().min(0).max(100_000),
  /** At or under this stock the CMS lists the variant as low and the shop says "Only N left". Unset = SHOP_LIMITS.lowStock. */
  lowStock: z.number().int().min(0).max(SHOP_LIMITS.lowStockMax).optional(),
})
export type VariantInput = z.infer<typeof VariantInputSchema>

/** A category's size chart: column headings (Chest, Length…), one row per size, and a note. */
export const SizeGuideSchema = z.object({
  columns: z.array(LocalizedSchema).max(8).default([]),
  rows: z.array(z.object({ size: SizeLabelSchema, values: z.array(z.string().max(24)).max(8) })).max(20).default([]),
  note: LocalizedSchema.default({}),
})
export type SizeGuide = z.infer<typeof SizeGuideSchema>
export const emptySizeGuide = (): SizeGuide => ({ columns: [], rows: [], note: {} })

/** The store fields of a product write, checked together (the API and the CMS both run this). */
export function shopIssues(input: { price: number | null; compareAtPrice: number | null; shop: ShopProduct; variants: VariantInput[] }): { path: (string | number)[]; message: string }[] {
  const issues: { path: (string | number)[]; message: string }[] = []
  if (input.compareAtPrice !== null && (input.price === null || input.compareAtPrice <= input.price)) {
    issues.push({ path: ['compareAtPrice'], message: 'The original price must be higher than the price' })
  }
  if (input.variants.length > 0 && input.price === null) issues.push({ path: ['price'], message: 'Set a price before adding stock' })
  const colors = new Set<string>()
  input.shop.colors.forEach((c, i) => {
    if (colors.has(c.key)) issues.push({ path: ['shop', 'colors', i, 'key'], message: 'Each colour needs its own key' })
    colors.add(c.key)
  })
  const sizes = new Set<string>()
  input.shop.sizes.forEach((s, i) => {
    if (sizes.has(s)) issues.push({ path: ['shop', 'sizes', i], message: 'Each size only once' })
    sizes.add(s)
  })
  const cells = new Set<string>()
  const skus = new Set<string>()
  input.variants.forEach((v, i) => {
    if (!colors.has(v.color)) issues.push({ path: ['variants', i, 'color'], message: 'Unknown colour' })
    if (!sizes.has(v.size)) issues.push({ path: ['variants', i, 'size'], message: 'Unknown size' })
    const cell = `${v.color}\u0000${v.size}`
    if (cells.has(cell)) issues.push({ path: ['variants', i], message: 'This colour and size is listed twice' })
    cells.add(cell)
    const sku = v.sku.toUpperCase()
    if (skus.has(sku)) issues.push({ path: ['variants', i, 'sku'], message: 'SKU used twice' })
    skus.add(sku)
  })
  return issues
}

/** Discount in whole percent, or null when the product is not on sale. */
export const discountPercent = (price: number | null, compareAt: number | null): number | null =>
  price !== null && compareAt !== null && compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : null
